import {useEffect} from 'react';
import {UserId} from '@legit.health/ui';
import {QueryClient} from '@tanstack/react-query';
import {DIAGNOSTIC_REPORT_HAS_PRIORITY_CHANGED} from '@/practitioner/hooks/diagnosticReport/api/diagnosticReportHasPriorityChangedEvent';
import {DIAGNOSTIC_REPORT_MARKED_AS_REVIEWED} from '@/practitioner/hooks/diagnosticReport/api/diagnosticReportMarkedAsReviewedEvent';
import {DiagnosticReportApiResponse} from '@/practitioner/interfaces/DiagnosticReport';
import {DIAGNOSTIC_REPORTS} from '@/shared/const/backend';
import {SubscribeToEventsOptions} from '@/shared/contexts/SubscribeToEventsContext';
import {useCompany} from '@/shared/contexts/UserContext/useUserContext';
import {DIAGNOSTIC_REPORT_CREATED_EVENT} from '@/shared/domainEvents/diagnosticReportCreatedEvent';
import {
  useDiagnosticReports,
  useSuspenseDiagnosticReports
} from '@/shared/hooks/useDiagnosticReports';
import {DIAGNOSTIC_REPORT_PATCHED_EVENT} from './diagnosticReportPatchedEvent';
import useSetDiagnosticReportQueryData from './useSetDiagnosticReportQueryData';

function invalidateQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    predicate: function (query) {
      const [first, second] = query.queryKey;
      if (!second) {
        return false;
      }
      return first === DIAGNOSTIC_REPORTS;
    }
  });
}

const subscribeToEventsOptions: SubscribeToEventsOptions = {
  dataKey: DIAGNOSTIC_REPORTS,
  subscriptions: [
    [DIAGNOSTIC_REPORT_CREATED_EVENT, invalidateQueries],
    [DIAGNOSTIC_REPORT_MARKED_AS_REVIEWED, invalidateQueries],
    [DIAGNOSTIC_REPORT_HAS_PRIORITY_CHANGED, invalidateQueries],
    [DIAGNOSTIC_REPORT_PATCHED_EVENT, invalidateQueries]
  ]
};

export function useFilteredDiagnosticReports({
  ids,
  practitionerId,
  page,
  isReviewed,
  undiagnosed,
  from,
  to,
  mostLikelyPathology,
  malignant,
  hasPriority,
  patientId,
  orderBy,
  limit
}: {
  ids?: string[];
  practitionerId?: UserId;
  patientId?: UserId;
  mostLikelyPathology?: string;
  page: number;
  isReviewed?: boolean;
  from?: string;
  to?: string;
  undiagnosed?: boolean;
  malignant?: boolean;
  hasPriority?: boolean;
  orderBy?: string;
  limit?: number;
}) {
  const setDiagnosticReportQueryData = useSetDiagnosticReportQueryData();
  const company = useCompany();

  const query = useDiagnosticReports<DiagnosticReportApiResponse>({
    searchParams: {
      ids,
      companyId: company.id,
      practitionerId,
      page: `${page}`,
      mostLikelyPathology,
      patientId,
      hasPriority,
      isReviewed,
      from,
      to,
      undiagnosed,
      malignant,
      orderBy,
      limit: limit ? `${limit}` : undefined
    },
    subscribeToEventsOptions
  });

  useEffect(
    function () {
      if (query.data?.data) {
        query.data.data.results.forEach((dr) => setDiagnosticReportQueryData(dr));
      }
    },
    [query.data?.data, setDiagnosticReportQueryData]
  );

  return query;
}

export function useSuspenseFilteredDiagnosticReports({
  practitionerId,
  ids,
  page,
  isReviewed,
  undiagnosed,
  from,
  to,
  mostLikelyPathology,
  malignant,
  hasPriority,
  patientId,
  orderBy,
  limit,
  encounterId
}: {
  ids?: string[];
  practitionerId?: UserId;
  patientId?: UserId;
  mostLikelyPathology?: string;
  page: number;
  isReviewed?: boolean;
  from?: string;
  to?: string;
  undiagnosed?: boolean;
  malignant?: boolean;
  hasPriority?: boolean;
  orderBy?: string;
  limit?: number;
  encounterId?: string;
}) {
  const setDiagnosticReportQueryData = useSetDiagnosticReportQueryData();

  const query = useSuspenseDiagnosticReports<DiagnosticReportApiResponse>({
    searchParams: {
      practitionerId,
      page: `${page}`,
      mostLikelyPathology,
      patientId,
      hasPriority,
      isReviewed,
      from,
      to,
      undiagnosed,
      ids,
      malignant,
      orderBy,
      encounterId,
      limit: limit ? `${limit}` : undefined
    },
    subscribeToEventsOptions
  });

  useEffect(
    function () {
      query.data.data.results.forEach((dr) => setDiagnosticReportQueryData(dr));
    },
    [query.data.data, setDiagnosticReportQueryData]
  );

  return query;
}
